// @flow

import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import { ReactForm, ReactFormField } from "@adrenalin/react-form";

import { customValidators } from "../../utils/validators";
import scrollToReacFormError from "../../utils/scrollToReacFormError";
import useAsyncState from "../../hooks/useAsyncState";
import { updateProfile, oktaLogin } from "../../actions/user";
import LinkHandler from "../atoms/LinkHandler";
import ServerErrorMessage from "../atoms/ServerErrorMessage";
import Button from "../atoms/Button";

const PersonalDetailsForm = (): React.Node => {
    const dispatch = useDispatch();
    const [state, localActions] = useAsyncState();
    const { profile, isOktaUser } = useSelector(
        (state: any): any => state.user
    );

    const handleFormSubmit = formData => {
        localActions.request();

        dispatch(updateProfile(formData))
            .then(res => {
                localActions.success();
            })
            .catch((error: any) => {
                console.error(error);

                localActions.fail(
                    error.message ||
                        "There was an issue updating your personal details."
                );
            });
    };

    return (
        <ReactForm
            className="form-inset"
            disableErrorScroll={true}
            validators={customValidators}
            onSuccess={handleFormSubmit}
            onError={scrollToReacFormError}
        >
            <h3 className="login-profile-body__content__title">
                Personal details
            </h3>

            <ServerErrorMessage>{state.error}</ServerErrorMessage>

            {isOktaUser ? (
                <p>
                    Your account uses your UTS ID profile. If you need to change
                    any of your personal details please{" "}
                    <LinkHandler
                        className="underline"
                        to="https://www.uts.edu.au/current-students/managing-your-course/your-student-info/personal-details"
                        target="_blank"
                    >
                        contact UTS
                    </LinkHandler>
                    .
                </p>
            ) : null}

            <ReactFormField
                className={isOktaUser ? "is-readonly" : ""}
                readOnly={isOktaUser}
                type="text"
                label="First name"
                name="first_name"
                validators={["customname"]}
                required={!isOktaUser}
                defaultValue={profile.first_name}
            />
            <ReactFormField
                className={isOktaUser ? "is-readonly" : ""}
                readOnly={isOktaUser}
                type="text"
                label="Last name"
                name="last_name"
                validators={["customname"]}
                required={!isOktaUser}
                defaultValue={profile.last_name}
            />
            <ReactFormField
                className={isOktaUser ? "is-readonly" : ""}
                readOnly={isOktaUser}
                type="email"
                label="Email"
                name="email"
                validators={["email"]}
                required={!isOktaUser}
                defaultValue={profile.email}
            />
            <ReactFormField
                type="text"
                label="Preferred Name"
                name="preferred_name"
                validators={["customname"]}
                defaultValue={profile.preferred_name}
            />
            <ReactFormField
                type="text"
                label="Mobile Number"
                name="mobile"
                validators={["phonenumber"]}
                defaultValue={profile.mobile}
            />

            <Button
                type="submit"
                styles={["green"]}
                inline={true}
                aria-label="Save"
                loading={state.loading}
                success={!!state.success}
            >
                Save
            </Button>

            {!isOktaUser ? (
                <p>
                    If you're a <b>UTS student</b> or <b>staff member</b>,
                    please
                    <LinkHandler
                        className="underline"
                        to="#"
                        onClick={() => dispatch(oktaLogin())}
                    >
                        connect your UTS email
                    </LinkHandler>{" "}
                    to access discounts.
                </p>
            ) : null}
        </ReactForm>
    );
};

export default PersonalDetailsForm;
